import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import './FirstPartyModal.scss';
import TableModal from '../../../../../common/Modal/TableModal/TableModal';
import Icon from '../../../../../common/Icon/Icon';
import Tooltip from '../../../../../common/Tooltip/Tooltip';
import { isDmpRecentSegment } from '../../../../../../utils/segmentUtils';

const FirstPartyModal = ({
  isOpen,
  modalTitle,
  firstPartySegments = [],
  firstPartyInput = {},
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const type = '1st party';

  const transformedData = useMemo(
    () =>
      firstPartySegments.map((segment) => ({
        segmentValue: segment?.dmp_segment_id,
        createdAt: segment?.created_at,
        name: segment?.name,
        sourceName: segment?.first_party_source_name,
        segmentSize: segment?.segment_size?.toLocaleString(),
        createdBy: segment?.created_by,
        expirationDate: segment?.expires_at,
        segment: segment,
      })),
    [firstPartySegments]
  );

  const renderDiscoveryUsers = (row) => (
    <div className="discovery-users-container">
      <span>{row.segmentSize}</span>
      {isDmpRecentSegment(row.segment) && (
        <Tooltip
          options={{ theme: 'grey' }}
          content="This segment was created recently. User counts may take a few days to stabilize."
        >
          <Icon iconId="info-italic" className="info-icon" />
        </Tooltip>
      )}
    </div>
  );
  renderDiscoveryUsers.displayName = 'renderDiscoveryUsers';

  const renderExpirationDate = (row) => {
    if (!row.segment?.expires_at) {
      return <span className="no-expiration">No expiration</span>;
    }

    const expirationDate = new Date(row.segment.expires_at);
    const now = new Date();
    const isExpired = expirationDate <= now;

    return (
      <span className={`expiration-date ${isExpired ? 'expired' : 'active'}`}>
        {expirationDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
        {isExpired && <span className="expired-indicator"> (Expired)</span>}
      </span>
    );
  };
  renderExpirationDate.displayName = 'renderExpirationDate';

  const columns = [
    {
      id: 'name',
      title: 'Segment',
      sortable: true,
      showTooltip: true,
      className: 'cell-name',
      minWidth: '200px',
      // Drives a grid track of minmax(200px, 50%): the primary column grows toward
      // half the table width (matching the 50% search bar), capped there and
      // truncated with an ellipsis past it.
      maxWidth: '50%',
    },
    {
      id: 'createdAt',
      title: 'Created On',
      sortable: true,
      className: 'cell-date',
      minWidth: '140px',
    },
    {
      id: 'sourceName',
      title: 'Source',
      sortable: true,
      className: 'cell-name',
      minWidth: '80px',
    },
    {
      id: 'segmentSize',
      title: 'Discovery Users',
      sortable: true,
      className: 'cell-number',
      template: renderDiscoveryUsers,
      minWidth: '140px',
    },
    {
      id: 'segmentValue',
      title: 'DMP Segment ID',
      sortable: true,
      className: 'cell-id',
      minWidth: '150px',
    },
    {
      id: 'expirationDate',
      title: 'Expiration Date',
      sortable: true,
      className: 'cell-date',
      template: renderExpirationDate,
      minWidth: '150px',
    },
  ];

  const handleRowClick = (row) => {
    if (onSubmit) {
      const selectedSegment = {
        ...firstPartyInput,
        type,
        ...row.segment,
      };
      onSubmit(selectedSegment);
    }
  };

  return (
    <div className="first-party-modal-component">
      <TableModal
        isOpen={isOpen}
        modalTitle={modalTitle}
        subtitle="Select a segment by clicking its row"
        data={transformedData}
        columns={columns}
        onRowClick={handleRowClick}
        onCancel={onCancel}
        searchPlaceholder="Search first party segments..."
        emptyMessage="No first party segments found"
        searchableFields={['name', 'sourceName', 'segmentValue', 'expirationDate']}
        defaultSort={{ key: 'createdAt', direction: 'desc' }}
        height="527px"
        isLoading={isLoading}
      />
    </div>
  );
};

FirstPartyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  modalTitle: PropTypes.string,
  firstPartySegments: PropTypes.array,
  firstPartyInput: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default FirstPartyModal;
